import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  getOrgQuotas,
  resetOrgQuotas,
  updateOrgQuotas,
  type OrgQuotaSnapshot,
  type OrgQuotaUpdateInput,
} from "@/services/quotaConfigApi";

const METRIC_LABEL: Record<string, string> = {
  invites: "Invitations",
  messages: "Messages",
  enrichments: "Enrichissements",
};

type MetricKey = "invites" | "messages" | "enrichments";
const METRIC_KEYS: MetricKey[] = ["invites", "messages", "enrichments"];

const FIELD_BY_METRIC: Record<MetricKey, keyof OrgQuotaUpdateInput> = {
  invites: "invites_quota_monthly",
  messages: "messages_quota_monthly",
  enrichments: "enrichments_quota_monthly",
};

export default function OrganizationQuotasSection({
  organizationId,
}: {
  organizationId: number;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [snap, setSnap] = useState<OrgQuotaSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<MetricKey, number>>({
    invites: 0,
    messages: 0,
    enrichments: 0,
  });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrgQuotas(organizationId, navigate);
      setSnap(data);
      setDraft({
        invites: data.metrics.invites.quota_monthly,
        messages: data.metrics.messages.quota_monthly,
        enrichments: data.metrics.enrichments.quota_monthly,
      });
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Chargement des quotas impossible",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [organizationId, navigate, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload: OrgQuotaUpdateInput = {};
      METRIC_KEYS.forEach((m) => {
        payload[FIELD_BY_METRIC[m]] = Math.max(0, Math.floor(draft[m]));
      });
      const updated = await updateOrgQuotas(organizationId, payload, navigate);
      setSnap(updated);
      setEditing(false);
      toast({ title: "Quotas modifiés", description: "Override appliqué (préservé au prochain billing)." });
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Modification impossible",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    const ok = await confirmDialog({
      title: "Reset complet des quotas",
      description:
        "Remet à zéro les compteurs mensuels de l'organisation ET vide les compteurs anti-détection glissants (Redis) de chaque compte membre. Action irréversible.",
      confirmLabel: "Reset",
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await resetOrgQuotas(organizationId, navigate);
      toast({
        title: "Quotas réinitialisés",
        description: `${res.flushed_accounts} compte(s) vidé(s), compteurs mensuels remis à zéro.`,
      });
      await load();
    } catch (err) {
      toast({
        title: "Erreur",
        description: err instanceof Error ? err.message : "Reset impossible",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border rounded p-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold">
          Quotas mensuels
          {snap && (
            <span className="ml-3 text-sm font-normal text-gray-500">tier {snap.tier}</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {snap && !snap.enforcement_enabled && (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Enforcement désactivé
            </span>
          )}
          {snap?.quota_overridden && (
            <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
              Override manuel
            </span>
          )}
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} disabled={loading || busy}>
              Modifier
            </Button>
          )}
          <Button size="sm" variant="destructive" onClick={handleReset} disabled={loading || busy}>
            <RotateCcw className="mr-1" size={14} /> Reset
          </Button>
        </div>
      </div>

      {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

      {snap && !loading && (
        <div className="space-y-3">
          {METRIC_KEYS.map((m) => {
            const metric = snap.metrics[m];
            const cap = Math.max(metric.quota_monthly, 1);
            const pct = Math.min(100, (metric.used_month / cap) * 100);
            return (
              <div key={m} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>{METRIC_LABEL[m]}</span>
                  {editing ? (
                    <Input
                      type="number"
                      min={0}
                      value={draft[m]}
                      onChange={(ev) =>
                        setDraft((p) => ({ ...p, [m]: Number(ev.target.value) }))
                      }
                      className="h-7 w-32"
                    />
                  ) : (
                    <span className="text-gray-600">
                      {metric.used_month} / {metric.quota_monthly}
                    </span>
                  )}
                </div>
                {!editing && <Progress value={pct} className="h-2" />}
              </div>
            );
          })}

          {editing && (
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditing(false);
                  setDraft({
                    invites: snap.metrics.invites.quota_monthly,
                    messages: snap.metrics.messages.quota_monthly,
                    enrichments: snap.metrics.enrichments.quota_monthly,
                  });
                }}
                disabled={busy}
              >
                Annuler
              </Button>
              <Button size="sm" onClick={handleSave} disabled={busy}>
                Enregistrer
              </Button>
            </div>
          )}

          {snap.usage_period_start && (
            <p className="text-xs text-gray-400">
              Début de période : {snap.usage_period_start}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
